import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import type * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartThemeService } from 'src/app/ui-kit/services/chart-theme.service';
import { buildBrandChartOptions } from 'src/app/ui-kit/chart/base-chart-options';

/** A single slice of the donut. */
export interface DonutChartSlice {
  name: string;
  value: number;
  /** Optional explicit color. Falls back to the brand palette. */
  color?: string;
}

/**
 * Brand-themed Highcharts donut chart. Good for proportional snapshots like
 * goal-health status or baseline coverage where a small set of named slices
 * sums to a meaningful whole.
 *
 * @example
 * <app-donut-chart
 *   [slices]="[
 *     { name: 'On Track', value: 12, color: 'var(--ion-color-success)' },
 *     { name: 'Behind', value: 4,  color: 'var(--ion-color-danger)' },
 *   ]"
 *   ariaLabel="Goal health breakdown"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-donut-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HighchartsChartComponent],
  host: { class: 'block' },
  template: `
    <div [style.height.px]="height()" [attr.role]="'img'" [attr.aria-label]="ariaLabel()">
      <highcharts-chart [options]="options()" style="width: 100%; height: 100%; display: block;" />
    </div>
  `,
})
export class DonutChartComponent {
  private readonly themeService = inject(ChartThemeService);

  /** Named slices that sum to the donut's total. */
  readonly slices = input.required<DonutChartSlice[]>();

  /** Chart height in px. @default 260 */
  readonly height = input<number>(260);

  /** Donut inner radius as a CSS-style percentage. @default 68 */
  readonly innerSize = input<number>(68);

  /** Accessible label. Required since charts convey information visually. */
  readonly ariaLabel = input.required<string>();

  /** Whether to render Highcharts' built-in legend below the chart. @default true */
  readonly showLegend = input<boolean>(true);

  readonly options = computed<Highcharts.Options>(() => {
    const base = buildBrandChartOptions(this.themeService.theme()) as Highcharts.Options;
    return {
      ...base,
      chart: { ...base.chart, type: 'pie', height: this.height() },
      plotOptions: {
        ...(base.plotOptions ?? {}),
        pie: {
          innerSize: `${this.innerSize()}%`,
          borderColor: 'var(--ion-color-surface)',
          borderWidth: 2,
          dataLabels: { enabled: false },
          showInLegend: true,
        },
      },
      legend: {
        ...(base.legend ?? {}),
        enabled: this.showLegend(),
        align: 'center',
        verticalAlign: 'bottom',
      },
      series: [
        {
          type: 'pie',
          data: this.slices().map(s => ({ name: s.name, y: s.value, color: s.color })),
        },
      ],
    };
  });
}
